<template lang="pug">
.documents

    //- Bestellbestätigung — web purchases only.
    ArtifactCard(
        v-if="isWebShop"
        :title="$t('Order confirmation')"
        icon="el-icon-fa-circle-check"
        :sent-at="form.confirmation_email_sent_at"
        :sent-email="findSentEmail('purchase-confirmation')"
        :warning="!form.confirmation_email_sent_at ? $t('Confirmation email has not been sent yet') : ''"
        :variant="!form.confirmation_email_sent_at ? 'warning' : 'default'"
        :date-time-format="dateTimeFormat"
    )
        template(#actions)
            el-button(
                v-if="hasPurchaserEmail"
                size="mini"
                :type="form.confirmation_email_sent_at ? 'default' : 'primary'"
                plain
                icon="el-icon-fa-paper-plane fas"
                @click="$emit('send-confirmation')"
            ) {{ form.confirmation_email_sent_at ? $t('Send new confirmation email') : $t('Send now') }}
            el-button(
                size="mini"
                icon="el-icon-fa-file-pdf far"
                @click="$emit('view-confirmation-pdf')"
            ) {{ $t('View PDF') }}

    //- Rechnung — both web and POS.
    ArtifactCard(
        :title="$t('Invoice')"
        icon="el-icon-fa-file-invoice"
        :sent-at="form.invoice_email_sent_at"
        :sent-email="findSentEmail('invoice')"
        :warning="!form.invoice_created_at ? $t('Invoice has not been generated yet') : (!form.invoice_email_sent_at && isWebShop ? $t('Invoice has not been sent yet') : '')"
        :variant="!form.invoice_created_at ? 'warning' : 'default'"
        :date-time-format="dateTimeFormat"
    )
        template(#actions)
            el-button(
                v-if="isWebShop && hasPurchaserEmail"
                size="mini"
                :type="form.invoice_email_sent_at ? 'default' : 'primary'"
                plain
                icon="el-icon-fa-paper-plane fas"
                @click="$emit('send-invoice')"
            ) {{ invoiceSendLabel }}
            el-button(
                size="mini"
                icon="el-icon-fa-file-pdf far"
                @click="$emit('view-invoice-pdf')"
            ) {{ invoiceViewLabel }}

    //- Kassenbon — POS only.
    ArtifactCard(
        v-if="isPos"
        :title="$t('Receipt')"
        icon="el-icon-fa-receipt"
        :sent-at="receiptCreatedAt"
        :trackable="false"
        :warning="!receiptCreatedAt ? $t('Receipt has not been generated yet') : ''"
        :variant="!receiptCreatedAt ? 'warning' : 'default'"
        :date-time-format="dateTimeFormat"
    )
        template(#actions)
            el-button(
                v-if="!receiptCreatedAt"
                size="mini"
                type="primary"
                plain
                icon="el-icon-fa-bolt fas"
                @click="$emit('render-receipt-pdf')"
            ) {{ $t('Generate now') }}
            el-button(
                v-else
                size="mini"
                icon="el-icon-fa-file-pdf far"
                @click="$emit('view-receipt-pdf')"
            ) {{ $t('View PDF') }}

    //- Stornierungen — one card aggregating all type=cancellation rows.
    ArtifactCard(
        v-if="cancellationRows.length"
        :title="$tc('cancellationsCardTitle', cancellationRows.length, { count: cancellationRows.length })"
        icon="el-icon-fa-ban"
        :trackable="false"
        :date-time-format="dateTimeFormat"
    )
        .cancellation-block(
            v-for="cancellation in cancellationRows"
            :key="`cancellation-${cancellation.id}`"
        )
            .cancellation-block__head
                .cancellation-block__title
                    span(v-if="cancellation.items && cancellation.items.length") {{ $tc('itemsCanceledAt', itemQty(cancellation), { count: itemQty(cancellation) }) }}
                    span(v-else) {{ $t('Partially canceled at') }}
                    |
                    nobr.cancellation-block__date {{ cancellation.created_at | timestamp2date(dateTimeFormat) }}
                .cancellation-block__refund(v-if="cancellation.refund_status === 'succeeded'")
                    i.el-icon-fa-check.fas
                    |  {{ $t('Refunded') }}
                .cancellation-block__refund.refund-failed(v-else-if="cancellation.refund_status === 'failed'")
                    i.el-icon-fa-exclamation-triangle.fas
                    |  {{ $t('Refund failed') }}

            .cancellation-block__email(v-if="cancellation.credit_note_sent_at")
                .cancellation-block__email-label
                    | {{ $t('Credit note email') }}
                    small.cancellation-block__email-when · {{ $t('sent at') }} {{ formatTimestamp(cancellation.credit_note_sent_at) }}
                EngagementStrip(
                    :sent-email="findCreditNoteSentEmail(cancellation)"
                    :fallback-sent-at="cancellation.credit_note_sent_at"
                    :date-time-format="dateTimeFormat"
                )

            .cancellation-block__actions
                el-button(
                    v-if="hasPurchaserEmail"
                    size="mini"
                    :type="cancellation.credit_note_sent_at ? 'default' : 'primary'"
                    plain
                    icon="el-icon-fa-paper-plane fas"
                    @click="$emit('send-credit-note', cancellation)"
                ) {{ cancellation.credit_note_sent_at ? $t('Send new credit note email') : $t('Send credit note email now') }}
                el-button(
                    size="mini"
                    icon="el-icon-fa-file-pdf far"
                    @click="$emit('view-credit-note-pdf', cancellation)"
                ) {{ $t('Credit Note') }}
                el-button(
                    v-if="cancellation.latestTillRoll"
                    size="mini"
                    icon="el-icon-fa-receipt fas"
                    @click="$emit('view-cancellation-receipt-pdf', cancellation)"
                ) {{ $t('Cancellation Receipt') }}

    //- Lieferschein — PDF only, no tracking. Not offered when nothing is delivered: a delivery
    //- note for an emailed ticket describes a parcel that will never exist.
    ArtifactCard(
        v-if="isWebShop && isShippable"
        :title="$t('Delivery note')"
        icon="el-icon-fa-truck-fast"
        :sent-at="form.delivery_note_created_at"
        :trackable="false"
        :date-time-format="dateTimeFormat"
    )
        template(#actions)
            el-button(
                size="mini"
                icon="el-icon-fa-file-pdf far"
                @click="$emit('view-delivery-note-pdf')"
            ) {{ $t('View PDF') }}

    //- Packliste — per-shipping-package PDFs + return labels. Same reasoning as the delivery note.
    ArtifactCard(
        v-if="isWebShop && isShippable"
        :title="$t('Packing slip')"
        icon="el-icon-fa-boxes-stacked"
        :trackable="false"
        :date-time-format="dateTimeFormat"
    )
        //- Card-level action — "full list" PDF spanning every package.
        //- Hidden when there's only one package (its slip PDF == this PDF).
        template(v-if="hasMultiplePackages" #header-action)
            el-button(
                size="mini"
                type="primary"
                plain
                icon="el-icon-fa-file-pdf far"
                @click="$emit('view-packing-list-pdf')"
            ) {{ $t('View full list PDF') }}

        ul.package-list(v-if="form.shippingPackages && form.shippingPackages.length")
            li.package-list__item(
                v-for="shippingPackage in form.shippingPackages"
                :key="`packing-${shippingPackage.id}`"
            )
                el-button(
                    size="mini"
                    icon="el-icon-fa-file-pdf far"
                    @click="$emit('view-shipping-package-slip-pdf', shippingPackage)"
                ) {{ packageLabel(shippingPackage) }} · {{ $t('View PDF') }}
                el-button(
                    v-if="shippingPackage.shipmentLabel"
                    size="mini"
                    icon="el-icon-fa-tag fas"
                    @click="$emit('open-shipment-label', shippingPackage)"
                ) {{ $t('Show shipping label') }}
                el-button(
                    v-if="shippingPackage.shipmentLabel && shippingPackage.shippingService === 'ups'"
                    size="mini"
                    icon="el-icon-fa-rotate-left fas"
                    @click="$emit('get-ups-return-label', shippingPackage)"
                ) {{ $t('Create return label (opens in new tab)') }}

    //- Versandbestätigungen — one card aggregating every shipping package
    //- whose confirmation email has been dispatched.
    ArtifactCard(
        v-if="shippingConfirmationPackages.length"
        :title="$t('Shipping confirmations')"
        icon="el-icon-fa-paper-plane"
        :trackable="false"
        :date-time-format="dateTimeFormat"
    )
        .sent-email-block(
            v-for="shippingPackage in shippingConfirmationPackages"
            :key="`shipping-conf-${shippingPackage.id}`"
        )
            .sent-email-block__head
                .sent-email-block__title
                    span(v-if="shippingPackage.pointOfSale") {{ $t('Package POS {name}', { name: shippingPackage.pointOfSale.name }) }}
                    span(v-else) {{ $t('Package {name}', { name: shippingPackage.name || shippingPackage.id }) }}
                nobr.sent-email-block__date {{ formatTimestamp(shippingPackage.shipping_email_sent_at) }}
            EngagementStrip(
                :sent-email="findShippingConfirmationSentEmail(shippingPackage)"
                :fallback-sent-at="shippingPackage.shipping_email_sent_at"
                :date-time-format="dateTimeFormat"
            )
            .sent-email-block__actions
                el-button(
                    size="mini"
                    icon="el-icon-fa-rotate fas"
                    @click="$emit('send-shipping-confirmation', shippingPackage.id)"
                ) {{ $t('Send shipping email again') }}

    //- Eventtickets — one row per dispatched ticket mail. No resend action:
    //- ResendBookingConfirmation is keyed by booking, and a SentEmail row
    //- carries no booking id to hand it.
    ArtifactCard(
        v-if="eventTicketSentEmails.length"
        :title="$tc('eventTicketsCardTitle', eventTicketSentEmails.length, { count: eventTicketSentEmails.length })"
        icon="el-icon-fa-ticket-alt"
        :trackable="false"
        :date-time-format="dateTimeFormat"
    )
        .sent-email-block(
            v-for="sentEmail in eventTicketSentEmails"
            :key="`event-ticket-${sentEmail.id}`"
        )
            .sent-email-block__head
                .sent-email-block__title {{ sentEmail.subject }}
                nobr.sent-email-block__date {{ formatTimestamp(sentEmail.created_at) }}
            EngagementStrip(
                :sent-email="sentEmail"
                :date-time-format="dateTimeFormat"
            )

    //- Widerrufe — dedicated card.
    Returns(
        v-if="hasReturnCancellations"
        :cancellations="form.cancellations"
        :purchaser-email="purchaserEmail"
        :date-time-format="dateTimeFormat"
        :auto-refundable="autoRefundable"
        :stock-options="stockOptions"
        :currency="currency"
        @approve="cancellation => $emit('approve-return', cancellation)"
        @resend-received="cancellation => $emit('resend-return-received', cancellation)"
        @resend-status-changed="cancellation => $emit('resend-return-status-changed', cancellation)"
        @refresh="$emit('refresh-purchase')"
    )
</template>

<i18n>
{
    "en": {
        "cancellationsCardTitle": "Cancellation | Cancellations ({count})",
        "eventTicketsCardTitle": "Ticket | Tickets ({count})",
        "itemsCanceledAt": "1 item canceled at | {count} items canceled at",
        "Package {name}": "Package {name}",
        "Package POS {name}": "Package POS {name}"
    },
    "de": {
        "Order confirmation": "Bestellbestätigung",
        "Invoice": "Rechnung",
        "Receipt": "Kassenbon",
        "Delivery note": "Lieferschein",
        "Packing slip": "Packliste",
        "Shipping confirmations": "Versandbestätigungen",
        "Package {name}": "Paket {name}",
        "Package POS {name}": "Paket POS {name}",
        "Show shipping label": "Versandlabel anzeigen",
        "Create return label (opens in new tab)": "Return-Label erstellen (öffnet in neuem Tab)",
        "View full list PDF": "Gesamt-PDF anzeigen",
        "Send shipping email again": "Versandbestätigung erneut versenden",
        "Send now": "Jetzt versenden",
        "View PDF": "PDF anzeigen",
        "Generate now": "Jetzt generieren",
        "Generate now and send by email": "Jetzt generieren und per Email versenden",
        "Generate now and view PDF": "Jetzt generieren und PDF anzeigen",
        "Send new confirmation email": "Neue Bestellbestätigung versenden",
        "Send new invoice email": "Neue Rechnung versenden",
        "Send credit note email now": "Gutschrift jetzt versenden",
        "Send new credit note email": "Gutschrift erneut versenden",
        "Credit note email": "Gutschrift-E-Mail",
        "Credit Note": "Gutschrift",
        "Cancellation Receipt": "Stornobeleg",
        "Confirmation email has not been sent yet": "Bestellbestätigung wurde noch nicht versendet",
        "Invoice has not been generated yet": "Rechnung wurde noch nicht generiert",
        "Invoice has not been sent yet": "Rechnung wurde noch nicht versendet",
        "Receipt has not been generated yet": "Kassenbon wurde noch nicht generiert",
        "Partially canceled at": "Teilweise storniert am",
        "Refunded": "Erstattet",
        "Refund failed": "Erstattung fehlgeschlagen",
        "sent at": "versendet am",
        "cancellationsCardTitle": "Stornierung | Stornierungen ({count})",
        "eventTicketsCardTitle": "Ticket | Tickets ({count})",
        "itemsCanceledAt": "1 Artikel storniert am | {count} Artikel storniert am"
    }
}
</i18n>

<script>
import _ from 'lodash';
import { timestamp2date } from '@/filters';
import ArtifactCard from '@/extensions/shop/views/Purchase/Form/Partials/ArtifactCard.vue';
import EngagementStrip from '@/extensions/shop/views/Purchase/Form/Partials/EngagementStrip.vue';
import Returns from '@/extensions/shop/views/Purchase/Form/Partials/Returns.vue';

export default {
    name: 'Documents',
    components: { ArtifactCard, EngagementStrip, Returns },
    filters: { timestamp2date },
    props: {
        form: {
            type: Object,
            required: true,
        },
        dateTimeFormat: {
            type: String,
            required: true,
        },
        stockOptions: {
            type: Array,
            default: () => [],
        },
        currency: {
            type: String,
            default: '€',
        },
    },
    computed: {
        autoRefundable() {
            return !!_.get(this.form, 'is_auto_refundable');
        },
        isPos() {
            return this.form.sold_by === 'point-of-sale' || this.form.sold_to === 'point-of-sale';
        },
        receiptCreatedAt() {
            return _.get(this.form, 'latestReceipt.created_at');
        },
        isWebShop() {
            return !this.isPos;
        },
        /**
         * Whether this order has anything to hand to a carrier. False for tickets and coupons - the
         * API answers it once (Purchase::requiresShipping) rather than each screen guessing.
         */
        isShippable() {
            return _.get(this.form, 'requires_shipping') !== false;
        },
        purchaserEmail() {
            return _.get(this.form, 'purchaser.email', '');
        },
        hasPurchaserEmail() {
            return !!(this.purchaserEmail && this.purchaserEmail.length);
        },
        cancellationRows() {
            return _.filter(_.get(this.form, 'cancellations') || [], c => c.type !== 'return');
        },
        hasReturnCancellations() {
            return _.some(_.get(this.form, 'cancellations') || [], c => c.type === 'return');
        },
        purchaseSentEmails() {
            return _.get(this.form, 'sentEmails') || [];
        },
        eventTicketSentEmails() {
            return this.purchaseSentEmails.filter(sentEmail => sentEmail.type === 'event-ticket');
        },
        shippingConfirmationPackages() {
            return _.filter(_.get(this.form, 'shippingPackages') || [], p => !!p.shipping_email_sent_at);
        },
        hasMultiplePackages() {
            return (_.get(this.form, 'shippingPackages') || []).length > 1;
        },
        // Three-state label tree for the invoice card. The "not yet
        // generated" branch must keep the legacy labels customers are
        // used to: "Jetzt generieren und per Email versenden" /
        // "Jetzt generieren und PDF anzeigen".
        invoiceSendLabel() {
            if (!this.form.invoice_created_at) return this.$t('Generate now and send by email');
            if (this.form.invoice_email_sent_at) return this.$t('Send new invoice email');
            return this.$t('Send now');
        },
        invoiceViewLabel() {
            if (!this.form.invoice_created_at) return this.$t('Generate now and view PDF');
            return this.$t('View PDF');
        },
    },
    methods: {
        findSentEmail(scope) {
            return _.findLast(this.purchaseSentEmails, se => se.type === scope) || null;
        },
        findCreditNoteSentEmail(cancellation) {
            const rows = cancellation.sentEmails || [];
            return _.findLast(rows, se => se.type === 'credit-note') || null;
        },
        findShippingConfirmationSentEmail(shippingPackage) {
            const rows = shippingPackage.sentEmails || [];
            return _.findLast(rows, se => se.type === 'shipping-package-sent') || null;
        },
        itemQty(cancellation) {
            if (!cancellation.items) return 0;
            return cancellation.items.reduce((acc, item) => acc + _.get(item, 'pivot.qty', 0), 0);
        },
        formatTimestamp(value) {
            return value ? timestamp2date(value, this.dateTimeFormat) : '';
        },
        packageLabel(shippingPackage) {
            if (shippingPackage.pointOfSale) {
                return this.$t('Package POS {name}', { name: shippingPackage.pointOfSale.name });
            }
            return this.$t('Package {name}', { name: shippingPackage.name || shippingPackage.id });
        },
    },
};
</script>

<style lang="sass" scoped>
.documents
    display: flex
    flex-direction: column
    gap: 8px
    // Action button rows in this column (Bestellbestätigung / Rechnung /
    // Lieferschein / Packliste / Widerrufe / sent-email-block) all use
    // flex + gap for spacing. Element UI adds margin-left:10px to adjacent
    // el-buttons via its own stylesheet, which survives the wrap to the
    // next line and shows up as a leading indent on the first button of
    // each wrapped row. Override once for any BEM `__actions` wrapper
    // inside this column.
    ::v-deep [class$="__actions"] .el-button + .el-button
        margin-left: 0

.cancellation-block
    // Single source of internal vertical rhythm (head → email → actions),
    // matching .return-block. padding/border below handle row separation.
    display: flex
    flex-direction: column
    gap: 12px
    border-top: 1px solid var(--border-color, #ebeef5)
    padding: 8px 0
    &:first-of-type
        border-top: 0
        padding-top: 4px
    &:last-of-type
        padding-bottom: 0

.cancellation-block__head
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: 8px

.cancellation-block__title
    display: flex
    flex-wrap: wrap
    align-items: baseline
    gap: 6px
    font-size: 13px

.cancellation-block__date
    color: var(--color-text-secondary, #606266)
    font-size: 12px

.cancellation-block__refund
    margin-left: auto
    font-size: 12px
    color: $--color-success
    i
        margin-right: 4px
    &.refund-failed
        color: $--color-warning

.cancellation-block__email
    display: flex
    flex-direction: column
    gap: 8px

.cancellation-block__email-label
    display: flex
    align-items: baseline
    flex-wrap: wrap
    gap: 4px
    font-size: 13px

.cancellation-block__email-when
    color: var(--color-text-secondary, #606266)
    font-size: 12px
    white-space: nowrap

.cancellation-block__actions
    display: flex
    flex-wrap: wrap
    gap: 8px

.package-list
    list-style: none
    padding: 0
    margin: 0
    display: flex
    flex-direction: column

.package-list__item
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: 6px
    padding: 8px 0
    border-top: 1px solid var(--border-color, #ebeef5)
    &:first-of-type
        border-top: 0
        padding-top: 0
    &:last-of-type
        padding-bottom: 0

// One dispatched mail per row — shared by the shipping-confirmation and
// event-ticket cards, both of which are just "here is what went out, and
// what the recipient did with it".
.sent-email-block
    // Same single-source rhythm as .cancellation-block / .return-block.
    display: flex
    flex-direction: column
    gap: 12px
    border-top: 1px solid var(--border-color, #ebeef5)
    padding: 8px 0
    &:first-of-type
        border-top: 0
        padding-top: 4px
    &:last-of-type
        padding-bottom: 0

.sent-email-block__head
    display: flex
    align-items: center
    flex-wrap: wrap
    gap: 8px

.sent-email-block__title
    display: flex
    align-items: center
    gap: 6px
    flex: 1
    font-size: 13px
    i
        color: var(--color-text-muted, #909399)

.sent-email-block__date
    color: var(--color-text-secondary, #606266)
    font-size: 12px

.sent-email-block__actions
    display: flex
    flex-wrap: wrap
    gap: 8px
</style>
